import React, { useMemo, useState } from 'react';
import InfoTip from '../../shared/InfoTip';
import { SkeletonText } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import { fmtCurrency, fmtPercent } from '../../shared/format';
import {
  CATEGORY_LABELS,
  ExportMenu,
  categorizeSearchTerm,
  downloadCsv,
  downloadPdf,
  makeExport,
  matchTypeBadge,
  stripMatchSymbols,
} from '../shared';
import Pagination from '../../shared/Pagination';
import ThinScroll from '../../shared/ThinScroll';

// Export spec — one column list drives CSV + PDF. CSV emits raw values + full
// headers; PDF emits formatted impressions/clicks/cost/CTR + abbreviated headers.
// Conversions stay raw in both (matches the prior hand-rolled behaviour).
const SEARCH_TERM_EXPORT_COLUMNS = [
  { header: 'Search Term', get: (term) => term.search_term },
  { header: 'Keyword', get: (term) => term.keyword_text || '' },
  { header: 'Campaign', get: (term) => term.campaign_name },
  { header: 'Match Type', pdfHeader: 'Match', get: (term) => term.match_type },
  { header: 'Impressions', pdfHeader: 'Impr.', get: (term) => term.impressions, pdf: (term) => term.impressions.toLocaleString() },
  { header: 'Clicks', get: (term) => term.clicks, pdf: (term) => term.clicks.toLocaleString() },
  { header: 'Cost', get: (term) => term.cost, pdf: (term) => fmtCurrency(term.cost) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (term) => term.conversions },
  { header: 'CTR', get: (term) => (term.ctr * 100).toFixed(2) + '%', pdf: (term) => fmtPercent(term.ctr) },
];
const SEARCH_TERM_EXPORT = makeExport(SEARCH_TERM_EXPORT_COLUMNS);

// 2026-07-23 — 25/page for consistency with the other GA tables (Campaign
// Performance, Geo, Non-Converting Terms). All rows are already loaded client-
// side (the page fetches every RPC page), so this is a pure display slice.
const PER_PAGE = 25;

const SearchTermExplorer = ({ searchTerms, loading, error, searchTermCategories, rangeStart, rangeEnd }) => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('cost');
  const [sortDir, setSortDir] = useState('desc');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);

  React.useEffect(() => { setPage(1); }, [searchTerms]);

  const filteredItems = useMemo(() => {
    if (!searchTerms?.items) return [];
    let items = searchTerms.items;

    if (category !== 'all') {
      items = items.filter((item) => categorizeSearchTerm(item.search_term, searchTermCategories).includes(category));
    }

    if (search.trim()) {
      const query = search.toLowerCase();
      items = items.filter((item) =>
        item.search_term.toLowerCase().includes(query) ||
        (item.keyword_text || '').toLowerCase().includes(query) ||
        item.campaign_name.toLowerCase().includes(query)
      );
    }

    items = [...items].sort((termA, termB) => {
      const aVal = termA[sortBy] ?? 0;
      const bVal = termB[sortBy] ?? 0;
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return items;
  }, [searchTerms, category, search, sortBy, sortDir, searchTermCategories]);

  const totalPages = Math.ceil(filteredItems.length / PER_PAGE) || 1;
  const pagedItems = filteredItems.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(1);
  };

  const exportCsv = () => {
    if (!filteredItems.length) return;
    downloadCsv(SEARCH_TERM_EXPORT.headers, filteredItems.map(SEARCH_TERM_EXPORT.toCsvRow), `search-terms-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!filteredItems.length) return;
    downloadPdf('Search Term Explorer', SEARCH_TERM_EXPORT.pdfHeaders, filteredItems.map(SEARCH_TERM_EXPORT.toPdfRow), `search-terms-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  const columns = [
    { key: 'search_term', label: 'Search Term', align: 'left' },
    { key: 'keyword_text', label: 'Keyword', align: 'left' },
    { key: 'campaign_name', label: 'Campaign', align: 'left' },
    { key: 'match_type', label: 'Match Type', align: 'left' },
    { key: 'impressions', label: 'Impr.', align: 'right' },
    { key: 'clicks', label: 'Clicks', align: 'right' },
    { key: 'cost', label: 'Cost', align: 'right' },
    { key: 'conversions', label: 'Conv.', align: 'right' },
    { key: 'ctr', label: 'CTR', align: 'right' },
  ];

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3 font-semibold inline-flex items-center">
        Search Term Explorer
        <InfoTip text="Actual user queries that triggered your ads, with cost and conversion data. Categorize and filter to spot wasted spend or expansion ideas." />
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-6 relative">
        {filteredItems.length > 0 && (
          <div className="absolute top-4 right-4"><ExportMenu onCsv={exportCsv} onPdf={exportPdf} /></div>
        )}
        {error ? (
          <ErrorState message={error} />
        ) : loading ? (
          <div className="py-4"><SkeletonText lines={10} /></div>
        ) : !searchTerms?.items?.length ? (
          <div className="flex items-center justify-center py-12 text-gray-400 dark:text-gray-500 text-sm">
            Search term data is not available for this time period. Try expanding your date range (e.g., 30 days) to view insights.
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 gap-3 mb-4 pr-10 min-[865px]:flex min-[865px]:flex-wrap">
              {[
                { label: 'Unique Terms', value: searchTerms.summary.total_unique_terms.toLocaleString(), tip: 'Count of distinct search queries (COUNT DISTINCT search_term). The table below lists more rows than this, because one term can run under several campaigns and match types — each of those is its own row.' },
                { label: 'Total Spend', value: fmtCurrency(searchTerms.summary.total_spend), tip: 'Total cost across all search terms in this period.' },
                { label: 'Converting Terms', value: searchTerms.summary.converting_terms.toLocaleString(), color: 'text-emerald-600 dark:text-emerald-400', tip: 'Distinct search terms with at least one conversion in this period.' },
                { label: 'Non-Converting Terms w/ Spend', value: searchTerms.summary.non_converting_terms_with_spend.toLocaleString(), color: 'text-amber-600 dark:text-amber-400', tip: 'Distinct search terms that had spend but no conversions in this period. Worth reviewing — though some may be seasonal or core terms.' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <InfoTip text={stat.tip} placement="bottom">
                    <div className="text-[12px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">{stat.label}</div>
                  </InfoTip>
                  <div className={`text-lg font-bold ${stat.color || 'text-gray-900 dark:text-gray-100'}`}>{stat.value}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4 max-[884px]:grid max-[884px]:grid-cols-3 max-[884px]:items-stretch max-[424px]:grid-cols-2">
              {[{ key: 'all', label: 'All', tip: 'Show all search terms regardless of category' }, ...CATEGORY_LABELS].map((cat) => (
                <div key={cat.key} className="group relative">
                  <button
                    type="button"
                    onClick={() => { setCategory(cat.key); setPage(1); }}
                    className={`max-[884px]:w-full max-[884px]:h-full px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
                      category === cat.key
                        ? 'bg-[#32B1FF] text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {cat.label}
                  </button>
                  {cat.tip && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                      <div className="bg-gray-900 text-white text-xs leading-relaxed rounded-lg px-3 py-2 shadow-2xl border border-white/20 whitespace-normal w-52 text-center">
                        {cat.tip}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-[6px] border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="w-full min-[885px]:w-auto min-[885px]:ml-auto relative max-[884px]:col-span-3 max-[424px]:col-span-2">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search terms…"
                  value={search}
                  onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                  className="pl-8 pr-3 py-1.5 text-xs border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full min-[885px]:w-52 focus:outline-none focus:ring-1 focus:ring-[#32B1FF]"
                />
              </div>
            </div>

            <ThinScroll className="responsive-card-table-650-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="responsive-card-table-650 w-full text-sm min-w-[1000px]" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    {/* Sort control is a real <button> inside the <th> (keyboard-reachable),
                        with aria-sort on the th to announce the active direction. */}
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        scope="col"
                        aria-sort={sortBy === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        className={`${col.align === 'right' ? 'text-right' : 'text-left'} py-2.5 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          title={`Sort by ${col.label}`}
                          className={`inline-flex items-center gap-1 select-none uppercase tracking-wide font-semibold hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/40 rounded-sm ${col.align === 'right' ? 'justify-end w-full' : ''}`}
                        >
                          {col.label}
                          {sortBy === col.key && (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                              {sortDir === 'asc'
                                ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                                : <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />}
                            </svg>
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                        No search terms match your filters.
                      </td>
                    </tr>
                  ) : pagedItems.map((item, index) => (
                    <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td data-label="Search Term" className="py-2.5 px-3 text-gray-900 dark:text-gray-100 font-medium max-[649px]:flex-wrap max-[344px]:flex-col max-[344px]:!items-start">
                        {item.search_term}
                        {categorizeSearchTerm(item.search_term, searchTermCategories).length > 0 && (
                          <span className="max-[2229px]:block max-[2229px]:mt-1 max-[649px]:basis-full max-[649px]:mt-1 max-[649px]:flex max-[649px]:flex-wrap max-[649px]:justify-end max-[344px]:basis-auto max-[344px]:grid max-[344px]:w-full max-[344px]:grid-cols-3 max-[344px]:gap-1">
                            {categorizeSearchTerm(item.search_term, searchTermCategories).map((cat) => (
                              <span key={cat} className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 max-[2229px]:text-center max-[2229px]:my-[2px] max-[344px]:ml-0 max-[344px]:w-full max-[344px]:text-center max-[344px]:my-0">
                                {CATEGORY_LABELS.find((category) => category.key === cat)?.label || cat}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td data-label="Keyword" className="py-2.5 px-3 text-gray-600 dark:text-gray-400 text-xs truncate">{stripMatchSymbols(item.keyword_text) || '—'}</td>
                      <td data-label="Campaign" className="py-2.5 px-3 text-gray-600 dark:text-gray-400 text-xs truncate">{item.campaign_name}</td>
                      <td data-label="Match Type" className="py-2.5 px-3">{matchTypeBadge(item.match_type)}</td>
                      <td data-label="Impr." className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.impressions.toLocaleString()}</td>
                      <td data-label="Clicks" className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.clicks.toLocaleString()}</td>
                      <td data-label="Cost" className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{fmtCurrency(item.cost)}</td>
                      <td data-label="Conv." className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{item.conversions}</td>
                      <td data-label="CTR" className="py-2.5 px-3 text-right text-gray-700 dark:text-gray-300">{fmtPercent(item.ctr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ThinScroll>

            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={filteredItems.length}
              perPage={PER_PAGE}
              label="rows"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchTermExplorer;
